import type { AxiosError } from "axios"
import axios from "axios"

import { refreshToken as callRefreshToken } from "@/api/refresh/api"
import customErrorCode from "@/enums/customErrorCode"
import { STATUS_MESSAGES } from "@/enums/httpStatus"
import { useUserStore } from "@/stores/user"

const baseURL = import.meta.env.DEV ? "/api" : import.meta.env.VITE_BASE_URL
const timeout = 20000

/* 不具備錯誤處理的 axios instance（只具備對於訊息的處理） */
export const instance = axios.create({
    baseURL,
    timeout,
})

instance.interceptors.request.use(
    function (config) {
        if (config.skipAuth) return config

        const token = useUserStore().formatUserToken
        if (!token) return Promise.reject(new axios.CanceledError())
        config.headers["Authorization"] = config.headers["Authorization"] ?? `Bearer ${token}`
        return config
    },
    function (error) {
        throw error
    }
)

instance.interceptors.response.use(
    function (response) {
        response.config.metadata = {
            ...response.config.metadata,
            endTime: new Date(),
        }
        return response
    },

    async function (error) {
        const response = error?.response

        // 當錯誤為 401 並且 VITE_USE_REFRESH_TOKEN 為 true 時，會嘗試刷新重新登入
        if (response?.status === 401 && import.meta.env.VITE_USE_REFRESH_TOKEN === "true") {
            const refreshSuccess = await handleTokenRefresh()
            // 當重新登入一次成功，會重新開始請求｜當重新登入一次失敗，會顯示錯誤
            if (refreshSuccess) return restartRequest(error)
        }

        error.message = getCustomErrorMessage(response?.status, response?.data, error.code)

        error.status = error?.response?.status ?? "--"
        error.backendCode = error?.response?.data?.code ?? "--"

        throw error
    }
)

export function getCustomErrorMessage(
    status: number | undefined,
    responseData: { code?: number } | undefined,
    errorCode: string | undefined
): string {
    if (errorCode === "ECONNABORTED") return "網路請求超時，請重新嘗試"

    if (status === 400) {
        const code = responseData?.code
        return (code && customErrorCode[code]) || STATUS_MESSAGES[400]
    }

    if (status === undefined) return STATUS_MESSAGES.default
    return STATUS_MESSAGES[status] || STATUS_MESSAGES.default
}

async function handleTokenRefresh(): Promise<boolean> {
    const userStore = useUserStore()

    try {
        const response = await callRefreshToken({
            data: { refresh: userStore.formatUserRefreshToken },
        })
        userStore.setUserToken(response.data)
        return true
    } catch {
        await userStore.logout()
        return false
    }
}

async function restartRequest(error: AxiosError) {
    const userStore = useUserStore()
    const newToken = userStore.formatUserToken
    if (!newToken || !error.config) throw error
    error.config.headers["Authorization"] = `Bearer ${newToken}`
    return instance(error.config)
}
